import {
  SkyBadge,
  SkyButton,
  SkyCard,
  SkyDivider,
  SkyInput,
  SkyLink,
  SkyMessageBar,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  DEFAULT_SUPPORT_URL,
  emailSignInProvider,
  signInProviderKind,
  type SignInProviderKind,
} from "@shared/types/authClientConfig";
import { useAuth } from "@/lib/auth/AuthContext";
import {
  autoSignInBlock,
  clearAutoSignIn,
  markAutoSignInAttempt,
  type SignInNotice,
} from "@/lib/auth/autoSignIn";
import { clearLastAccount, maskAddress, readLastAccount } from "@/lib/auth/lastAccount";
import { forgetProviderChoice } from "@/lib/auth/providerChoice";
import { idleLocked } from "@/lib/auth/sessionSync";
import {
  SIGN_IN_ERROR_TITLES,
  isMsalError,
  signInErrorView,
  type SignInErrorView,
} from "@/lib/auth/signInErrors";
import { DEV_PERSONAS, WALKTHROUGH_PERSONA_ROLE } from "@/lib/auth/devTokenProvider";
import type { AuthProviderOption, SignInOptions } from "@/lib/auth/types";
import { currentCorrelationId } from "@/lib/correlation";
import { InlineErrorBar, useInlineError, type InlineFailure } from "@/lib/feedback/InlineErrorBar";
import { ArrowRightIcon } from "@/lib/icons";

/**
 * The shared Skypoint artwork (issue #1172), copied once from the skychat sign-in page into
 * `client/public/brand/` and served by this app: never linked from the old B2C storage account,
 * which goes away with the B2C migration. The two background files are decorative.
 *
 * The logo is raster artwork in a near-black wordmark, so it cannot follow the theme the way
 * `BrandLogo`'s `currentColor` does: `logoDark` is the same artwork with the black and white parts
 * swapped (the amber dot kept), shown instead while the page is dark (PR #1261 review).
 */
export const SIGN_IN_ASSETS = {
  logo: "/brand/skypoint-logo.svg",
  logoDark: "/brand/skypoint-logo-dark.svg",
  pattern: "/brand/sign-in-background-pattern.svg",
  cornerShadow: "/brand/sign-in-corner-shadow.svg",
} as const;

/**
 * The three kinds of provider the page knows (round 10, C3): the workforce provider is Microsoft,
 * a google.com domain hint is Google, and anything else (External ID's own accounts) signs in
 * with an email address. A button's words come from its kind, never from the configured label,
 * so an issuer's name ("DEV", "External ID") is never on the page.
 */
export type ProviderKind = SignInProviderKind;

const PROVIDER_WORDS: Record<ProviderKind, string> = {
  email: "email",
  microsoft: "Microsoft",
  google: "Google",
};

export function providerKind(option: AuthProviderOption): ProviderKind {
  return signInProviderKind(option);
}

/**
 * Every string on the page, in one place: the owner may change the copy later (2026-09-28).
 * Issue #1172 (owner, 2026-10-15): one shared Skypoint sign-in page that names the product.
 */
export const SIGN_IN_COPY = {
  title: "Sign in to skyData",
  logoAlt: "Skypoint",
  emailLabel: "Email",
  continue: "Continue",
  emailPlaceholder: "name@company.com",
  invalidEmail: "Enter an email address, like name@example.com.",
  firstTime: "First time here? Select Continue, then choose Forgot password to set your password.",
  passwordHelper: "You enter your password on the secure sign-in page, not here.",
  signInWith: (kind: ProviderKind) => `Sign in with ${PROVIDER_WORDS[kind]}`,
  signInAgainWith: (kind: ProviderKind) => `Sign in again with ${PROVIDER_WORDS[kind]}`,
  continueAs: (maskedAddress: string) => `Continue as ${maskedAddress}`,
  useAnotherAccount: "Use another account",
  or: "OR",
  googleSoon: "Available soon",
  supportPrompt: "Questions?",
  supportLink: "Contact Support",
  oneAccount: "One Skypoint account for skyData and more.",
  copyright: (year: number) => `© ${year} Skypoint Cloud Inc. All Rights Reserved`,
} as const;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Whether the address asks for the choice of providers: `/sign-in?choose` always shows every way
 * to sign in (#1644).
 */
function chooseRequested(): boolean {
  try {
    return new URLSearchParams(window.location.search).has("choose");
  } catch {
    return false;
  }
}

/** Microsoft's four-square mark. Brand artwork, so its fills are the mark's own colours. */
function MicrosoftGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 21 21"
      className={className}
      aria-hidden="true"
      focusable="false"
      data-testid="microsoft-glyph"
    >
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

/** Google's "G". Brand artwork, so its fills are the mark's own colours. */
function GoogleGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 48 48"
      className={className}
      aria-hidden="true"
      focusable="false"
      data-testid="google-glyph"
    >
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/** The glyph a provider's button carries; an email provider has none. */
const GLYPHS: Record<ProviderKind, typeof MicrosoftGlyph | undefined> = {
  email: undefined,
  microsoft: MicrosoftGlyph,
  google: GoogleGlyph,
};

/** A sign-in MSAL refused to start: it never left the page, so it "did not start" (#2006). */
function startRefusalView(cause: unknown): SignInErrorView {
  const view = signInErrorView(cause);
  return view.cancelled ? view : { ...view, title: SIGN_IN_ERROR_TITLES.notStarted };
}

/**
 * The configuration's error or a refused sign-in (issue #2006), a failed attempt (with its
 * reference id), and the notice. A refused sign-in reads `signInErrorView`'s plain sentence,
 * never Microsoft's text, with its code and the reference id in small text for support.
 */
function SignInMessages({
  error,
  failure,
  notice,
}: {
  error: SignInErrorView | null;
  failure: InlineFailure | null;
  notice: SignInNotice | undefined;
}) {
  return (
    <>
      {error ? (
        <div className="mt-4">
          <SkyMessageBar
            intent={error.cancelled ? "warning" : "error"}
            layout="multiline"
            title={error.title}
            message={
              <>
                {error.message}
                {error.code ? (
                  <SkyText size={200} color="muted" block testId="sign-in-error-code">
                    Code {error.code}
                  </SkyText>
                ) : null}
                {/* The same reference id the notices and the server logs carry
                  (API-STANDARD §4): a person reporting this has one thing to
                  quote, and support has one thing to look up. */}
                <SkyText size={200} color="muted" block>
                  Reference id {currentCorrelationId()}
                </SkyText>
              </>
            }
            testId="sign-in-error"
          />
        </div>
      ) : null}

      {failure ? (
        <div className="mt-4">
          <InlineErrorBar
            error={failure}
            // A retryable failure keeps the bar's own "Try again" title.
            title={failure.retryable ? undefined : "Sign-in did not start"}
            testId="sign-in-failure"
          />
        </div>
      ) : null}

      {notice ? (
        <div className="mt-4">
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            title={notice.title}
            message={
              <>
                {notice.message}
                {notice.code ? (
                  <SkyText size={200} color="muted" block testId="sign-in-notice-code">
                    Code {notice.code}
                  </SkyText>
                ) : null}
                <SkyText size={200} color="muted" block>
                  Reference id {notice.correlationId}
                </SkyText>
              </>
            }
            testId="sign-in-notice"
          />
        </div>
      ) : null}
    </>
  );
}

/**
 * Sign-in (FR-10, LOCAL-DEV §3; redesigned 2026-09-28 after the owner's "replicate the login
 * page in https://skydata.skypoint.ai/", built new rather than copied; laid out by provider kind
 * in round 10, C3).
 *
 * Issue #1172 (owner, 2026-10-15): the shared Skypoint sign-in page. One centered card on the
 * skychat background pattern and corner shadow (no dark side panel): the Skypoint logo, "Sign in
 * to skyData", the ways to sign in, and an Atlassian style footer ("One Skypoint account for
 * skyData and more.", Contact Support). The copyright sits under the card. The card, top to
 * bottom:
 *
 * 1. Email first. With an email provider: the Email box, then the primary "Continue", which
 *    starts it with the address as `login_hint` (MSAL `loginRedirect` `loginHint`), so the
 *    hosted page opens on that account. A malformed address is refused beside the field; an
 *    empty one opens the hosted page with no hint. The password is typed on the provider's
 *    hosted page, never here, and the address is never logged. There is no sign-up. A second
 *    email provider gets no button: the words could not tell the two apart.
 * 2. "Sign in with Microsoft", one per workforce provider.
 * 3. "Sign in with Google" (owner, 2026-09-28: "Remember to add both Sign in with Microsoft and
 *    Google buttons"): a configured google.com provider's own button, else disabled, described
 *    by "Available soon", starting nothing. One authorised email may use any method; the rule
 *    that links a second method to an account is `docs/auth/sso.md`.
 *
 * The OR divider sits only between the email form and the buttons. Without an email provider
 * (Microsoft only, as the next environment starts) the first button is the primary one. The
 * primary action is always `sign-in-sso`, which the e2e setup and the guide's screenshots wait
 * for; every other button is `sign-in-with-<id>`, the placeholder `sign-in-with-google`.
 *
 * After a notice every provider keeps its name; the button of the provider this tab booted reads
 * "Sign in again with ..." (the email form's stays "Continue") and has focus. With an account
 * remembered (`lastAccount.ts`) the card offers only "Continue as a•••@x.com", which tries a
 * silent sign-in first, and "Use another account", which forgets it and shows every way in; a
 * button pressed after that opens the provider's account picker with no hint. After an idle logoff every button tries the address
 * that was signed in silently first, then redirects with it.
 *
 * **With one provider this is still the fallback, not the front door.** An unauthenticated
 * visit starts the redirect by itself (`lib/auth/autoSignIn.ts`); what lands here is a session
 * that ended or a sign-in that did not, and `notice` says which. With more than one provider
 * there is a choice to make, so an unauthenticated visit lands here (`useAutoSignIn`).
 *
 * With `VITE_DEV_AUTH=1` there is no provider to redirect to, so the card lists the four seeded
 * personas instead of the form, one per role (`signIn.devAuth.test.tsx` pins the exclusion).
 * Focus lands where the person starts: the Email box when the email form shows and no notice is
 * up (email first, so typing goes straight in), else the primary action, or after a notice the
 * button that reads "Sign in again with ..." (or the email form's Continue).
 */
export default function SignInPage({ notice }: { notice?: SignInNotice } = {}) {
  const { signIn, kind, error, status, retry, providers, supportUrl, activeProviderId } = useAuth();
  // Session 10 (ux-s10 §3): sign-in has no page shell and so no notice region. A failed attempt
  // is said here, in the page's own error slot, with the reference id support looks it up by.
  const failure = useInlineError();
  const [pending, setPending] = useState<string | null>(null);
  // Issue #2006: a sign-in MSAL refused to start (a blocked window, one already in progress).
  const [refused, setRefused] = useState<SignInErrorView | null>(null);
  const [email, setEmail] = useState("");
  const [emailInvalid, setEmailInvalid] = useState(false);
  // #1644: `/sign-in?choose` is the guaranteed way back to every way in. It forgets the remembered
  // provider (below) and shows the whole choice, as "Use another account" does.
  const [choose] = useState(chooseRequested);
  // Read once; "Use another account" forgets it and the page shows every way in.
  const [remembered, setRemembered] = useState(() => (choose ? null : readLastAccount()));
  const [anotherAccount, setAnotherAccount] = useState(choose);
  const primaryAction = useRef<HTMLButtonElement>(null);
  const emailField = useRef<HTMLInputElement>(null);
  const googleSoonId = useId();

  const options = providers ?? [];
  const defaultOption = options.find((option) => option.isDefault) ?? options[0];
  const emailOption = emailSignInProvider(options, (option) => option.isDefault);
  // No provider listed (a build without a setup): the form starts the tab's own provider.
  const hasEmailForm = emailOption !== undefined || options.length === 0;
  // Microsoft before Google whatever order the configuration lists them in.
  const buttons = [
    ...options.filter((option) => providerKind(option) === "microsoft"),
    ...options.filter((option) => providerKind(option) === "google"),
  ];
  const googleConfigured = buttons.some((option) => providerKind(option) === "google");
  const formKey = emailOption?.id ?? "sso";
  const primaryKey = hasEmailForm ? formKey : (buttons[0]?.id ?? formKey);
  // The newest refusal first: a sign-in MSAL refused to start after a redirect refusal (#2006).
  const shownError = refused ?? (error ? signInErrorView(error) : null);
  const again = notice !== undefined || error !== null;
  // The provider a notice is about: the one this tab booted (the remembered choice).
  const tabProvider = options.find((option) => option.id === activeProviderId) ?? defaultOption;
  const continueOption = remembered
    ? options.find((option) => option.id === remembered.providerId)
    : undefined;
  const showContinue =
    kind === "msal" && status !== "failed" && remembered !== null && continueOption !== undefined;
  const renderedKeys = [...(hasEmailForm ? [formKey] : []), ...buttons.map((option) => option.id)];
  const againKey = again && tabProvider ? tabProvider.id : undefined;
  // The control that has focus, and after a notice the one that reads "Sign in again with ...".
  const focusKey = againKey && renderedKeys.includes(againKey) ? againKey : primaryKey;

  // Email first: with the email form up and nothing to answer, typing goes straight into Email.
  const focusEmail =
    hasEmailForm && !again && !showContinue && kind !== "dev" && status !== "failed";

  useEffect(() => {
    if (choose) forgetProviderChoice();
  }, [choose]);

  useEffect(() => {
    const target = focusEmail && emailField.current ? emailField.current : primaryAction.current;
    target?.focus();
  }, [showContinue, focusEmail]);

  /**
   * After an idle logoff (the lock), one click signs back in as the address that was signed in:
   * silently while the identity provider's session stands, else through its page with the
   * address filled in (round 9 review #10; round 10, C3: no account picker).
   */
  const lockedOptions = (): SignInOptions => {
    if (!idleLocked()) return {};
    const hint = autoSignInBlock()?.loginHint ?? remembered?.address;
    return hint ? { loginHint: hint, silentFirst: true } : {};
  };

  /** "Use another account" was pressed: the account picker, and no remembered address. */
  const howToStart = (): SignInOptions =>
    anotherAccount ? { selectAccount: true } : lockedOptions();

  const start = async (key: string, hint?: string, signInOptions?: SignInOptions) => {
    setPending(key);
    failure.clear();
    setRefused(null);
    // Asking by hand replaces whatever the automatic path last recorded. The
    // new attempt is marked in its place so that a redirect which comes back
    // without a session still lands here with a reason rather than starting
    // another one (see `autoSignIn.ts`).
    if (kind === "msal") {
      clearAutoSignIn();
      markAutoSignInAttempt();
    }
    try {
      await signIn(hint, signInOptions);
    } catch (cause) {
      if (isMsalError(cause)) setRefused(startRefusalView(cause));
      else failure.show(cause, "Sign-in failed. Try again.");
    } finally {
      setPending(null);
    }
  };

  const submitEmail = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const address = email.trim();
    if (address && !EMAIL_PATTERN.test(address)) {
      setEmailInvalid(true);
      return;
    }
    setEmailInvalid(false);
    const base: SignInOptions = emailOption ? { providerId: emailOption.id } : {};
    const how = howToStart();
    // A typed address is the person's own choice: the redirect with it, never a silent try for
    // an address this browser did not remember.
    const typed = address !== "" && address.toLowerCase() !== how.loginHint?.toLowerCase();
    void start(
      formKey,
      undefined,
      typed
        ? { ...base, ...(how.selectAccount ? { selectAccount: true } : {}), loginHint: address }
        : { ...base, ...how },
    );
  };

  const continueAsRemembered = () => {
    if (!remembered || !continueOption) return;
    void start("continue", undefined, {
      providerId: continueOption.id,
      loginHint: remembered.address,
      silentFirst: true,
    });
  };

  const useAnotherAccount = () => {
    clearLastAccount();
    setRemembered(null);
    setAnotherAccount(true);
  };

  /** "Sign in with Microsoft", or "Sign in again with Microsoft" on the provider a notice is about. */
  const words = (key: string, providerKindOf: ProviderKind) =>
    again && key === focusKey
      ? SIGN_IN_COPY.signInAgainWith(providerKindOf)
      : SIGN_IN_COPY.signInWith(providerKindOf);

  function renderContinue(option: AuthProviderOption, address: string): ReactNode {
    return (
      <div className="mt-6 flex flex-col gap-2">
        <SkyButton
          ref={primaryAction}
          appearance="primary"
          className="w-full"
          icon={GLYPHS[providerKind(option)]}
          loading={pending === "continue"}
          disabled={pending !== null}
          testId="sign-in-continue"
          onClick={continueAsRemembered}
        >
          {SIGN_IN_COPY.continueAs(maskAddress(address))}
        </SkyButton>
        <SkyButton
          appearance="transparent"
          className="w-full"
          disabled={pending !== null}
          testId="sign-in-use-another"
          onClick={useAnotherAccount}
        >
          {SIGN_IN_COPY.useAnotherAccount}
        </SkyButton>
      </div>
    );
  }

  function renderProviderButton(option: AuthProviderOption): ReactNode {
    const optionKind = providerKind(option);
    const primary = !hasEmailForm && option.id === primaryKey;
    return (
      <SkyButton
        key={option.id}
        ref={option.id === focusKey ? primaryAction : undefined}
        appearance={primary ? "primary" : "outline"}
        className="w-full"
        icon={GLYPHS[optionKind]}
        loading={pending === option.id}
        disabled={pending !== null}
        testId={primary ? "sign-in-sso" : `sign-in-with-${option.id}`}
        onClick={() => void start(option.id, undefined, { providerId: option.id, ...howToStart() })}
      >
        {words(option.id, optionKind)}
      </SkyButton>
    );
  }

  function renderSignInAction(): ReactNode {
    if (status === "failed") {
      return (
        // Session 9 contract §2.5: nothing to sign in WITH until the
        // configuration loads, so the one action is to load it again.
        <div className="mt-6">
          <SkyButton
            ref={primaryAction}
            appearance="primary"
            className="w-full"
            testId="sign-in-retry"
            onClick={retry}
          >
            Retry
          </SkyButton>
        </div>
      );
    }
    if (kind === "dev") {
      return (
        <div className="mt-6">
          <div className="flex items-center gap-2">
            <SkyText as="h2" size={400} weight="semibold">
              Development sign-in
            </SkyText>
            <SkyBadge appearance="tint" color="warning">
              Dev only
            </SkyBadge>
          </div>
          <SkyText as="p" size={200} color="muted" block className="mt-1">
            Tokens come from the local issuer, not from your identity provider. Choose the role to
            sign in as.
          </SkyText>
          <div className="mt-4 flex flex-col gap-2">
            {DEV_PERSONAS.map((persona, index) => (
              <SkyButton
                key={persona.email}
                ref={index === 0 ? primaryAction : undefined}
                appearance="secondary"
                className="justify-between"
                loading={pending === persona.email}
                disabled={pending !== null}
                // The walkthrough drives one control, so the role it signs
                // in as (LOCAL-DEV §4 step 2) carries the bare `dev-sign-in`
                // id; the other three stay addressable by role for the steps
                // that need a Viewer.
                testId={
                  persona.role === WALKTHROUGH_PERSONA_ROLE
                    ? "dev-sign-in"
                    : `dev-sign-in-${persona.role}`
                }
                onClick={() => void start(persona.email, persona.email)}
              >
                <span className="flex flex-col items-start">
                  <span className="font-medium">{persona.label}</span>
                  <span className="text-xs text-muted-foreground">{persona.email}</span>
                </span>
                <ArrowRightIcon className="h-4 w-4" />
              </SkyButton>
            ))}
          </div>
        </div>
      );
    }
    if (showContinue && remembered && continueOption) {
      return renderContinue(continueOption, remembered.address);
    }
    return (
      <div className="mt-6">
        {hasEmailForm ? (
          <>
            <form noValidate onSubmit={submitEmail} className="flex flex-col gap-4">
              <SkyInput
                ref={emailField}
                label={SIGN_IN_COPY.emailLabel}
                type="email"
                name="email"
                autoComplete="username"
                placeholder={SIGN_IN_COPY.emailPlaceholder}
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (emailInvalid) setEmailInvalid(false);
                }}
                validationState={emailInvalid ? "error" : "none"}
                validationMessage={emailInvalid ? SIGN_IN_COPY.invalidEmail : undefined}
                hint={`${SIGN_IN_COPY.firstTime} ${SIGN_IN_COPY.passwordHelper}`}
                testId="sign-in-email"
              />
              <SkyButton
                ref={formKey === focusKey ? primaryAction : undefined}
                type="submit"
                appearance="primary"
                className="w-full"
                loading={pending === formKey}
                disabled={pending !== null}
                testId="sign-in-sso"
              >
                {SIGN_IN_COPY.continue}
              </SkyButton>
            </form>
            <SkyDivider className="my-6">{SIGN_IN_COPY.or}</SkyDivider>
          </>
        ) : null}
        <div className="flex flex-col gap-2">
          {buttons.map(renderProviderButton)}
          {googleConfigured ? null : (
            // No onClick: native `disabled` already swallows the click, and there is no
            // provider to start.
            <div className="flex flex-col gap-1">
              <SkyButton
                appearance="outline"
                className="w-full"
                icon={GoogleGlyph}
                disabled
                aria-describedby={googleSoonId}
                testId="sign-in-with-google"
              >
                {SIGN_IN_COPY.signInWith("google")}
              </SkyButton>
              <SkyText id={googleSoonId} size={200} color="muted" align="center" block>
                {SIGN_IN_COPY.googleSoon}
              </SkyText>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-background">
      {/* The skychat background: the corner shadow top right, the diamond pattern along the
          bottom. Decorative, behind the card, and never in the way of a click. */}
      <img
        src={SIGN_IN_ASSETS.cornerShadow}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-0 w-[689px] max-w-none select-none dark:opacity-40"
        data-testid="sign-in-corner-shadow"
      />
      <img
        src={SIGN_IN_ASSETS.pattern}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 w-full select-none dark:opacity-40"
        data-testid="sign-in-background-pattern"
      />
      <main
        id="main-content"
        className="relative flex flex-1 flex-col items-center justify-center gap-6 px-4 py-12"
        role="main"
      >
        <SkyCard className="w-full max-w-md p-8">
          <div className="flex justify-center" data-testid="brand-logo">
            {/* One of the two shows: `display: none` keeps the other out of the accessibility
                tree too, so a screen reader hears "Skypoint" once. */}
            <img
              src={SIGN_IN_ASSETS.logo}
              alt={SIGN_IN_COPY.logoAlt}
              width={156}
              height={50}
              className="h-10 w-auto dark:hidden"
              data-testid="skypoint-logo"
            />
            <img
              src={SIGN_IN_ASSETS.logoDark}
              alt={SIGN_IN_COPY.logoAlt}
              width={156}
              height={50}
              className="hidden h-10 w-auto dark:block"
              data-testid="skypoint-logo-dark"
            />
          </div>
          <SkyText as="h1" size={600} weight="semibold" align="center" block className="mt-6">
            {SIGN_IN_COPY.title}
          </SkyText>

          <SignInMessages error={shownError} failure={failure.error} notice={notice} />

          {renderSignInAction()}

          <footer className="mt-6 flex flex-col items-center gap-2 text-center">
            <SkyDivider />
            <SkyText as="p" size={200} color="muted" align="center" block className="mt-2">
              {SIGN_IN_COPY.oneAccount}
            </SkyText>
            <SkyText as="p" size={200} color="muted" align="center" block>
              {SIGN_IN_COPY.supportPrompt}{" "}
              <SkyLink
                href={supportUrl ?? DEFAULT_SUPPORT_URL}
                inline
                appearance="subtle"
                underline
              >
                {SIGN_IN_COPY.supportLink}
              </SkyLink>
            </SkyText>
          </footer>
        </SkyCard>

        <SkyText as="p" size={200} color="muted" block>
          {SIGN_IN_COPY.copyright(new Date().getFullYear())}
        </SkyText>
      </main>
    </div>
  );
}
